import React, { useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { cleanMissingDataNote } from '../../pages/ItineraryPage.utils';

/* ═══════════════════════════════════════════
   POSITIONED WRAPPER — sits over the map
   ═══════════════════════════════════════════ */

const Anchor = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;

  ${props => props.$isMobile && css`
    z-index: 12;
    right: 12px;
    bottom: ${props.$sheetPx ? `${props.$sheetPx + 12}px` : 'calc(65% + 12px)'};
  `}
`;

/* ─── Single trigger FAB ─── */
const TriggerButton = styled(motion.button)`
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
  }

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(255, 255, 255, 0.97);
  }

  ${props => props.$isMobile && css`
    padding: 8px 10px;
    font-size: 12px;
  `}
`;

const TriggerIcon = styled.span`
  font-size: 15px;
  line-height: 1;
`;

const Badge = styled.span`
  background: #2563eb;
  color: white;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

/* ─── Dropdown panel ─── */
const DropdownPanel = styled(motion.div)`
  pointer-events: auto;
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 300px;
  max-height: 420px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(255, 255, 255, 0.98);
  }

  ${props => props.$isMobile && css`
    width: 280px;
    max-height: 360px;
  `}
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
`;

const PanelTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
`;

const PanelClose = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 8px;
  line-height: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #6b7280;
  }
`;

const PanelScroll = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 8px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); border-radius: 10px; }
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.08) transparent;
`;

/* ─── Category row (collapsed) ─── */
const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  cursor: pointer;
  transition: background 0.15s;
  user-select: none;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  ${props => props.$active && css`
    background: rgba(37, 99, 235, 0.06);
  `}
`;

const CategoryIcon = styled.span`
  font-size: 16px;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$bg || '#f3f4f6'};
  border-radius: 10px;
`;

const CategoryLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex: 1;
`;

const CategoryChevron = styled.span`
  font-size: 11px;
  color: #9ca3af;
  transition: transform 0.2s;
  transform: ${props => props.$open ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

/* ─── Expanded detail area ─── */
const DetailArea = styled(motion.div)`
  padding: 0 12px 8px 50px;
  max-height: 180px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); border-radius: 10px; }
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.08) transparent;
`;

const ContentText = styled.p`
  font-size: 12px;
  line-height: 1.55;
  color: #4b5563;
  margin: 0;
`;

const ContentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ContentListItem = styled.li`
  font-size: 12px;
  line-height: 1.5;
  color: #4b5563;
  padding-left: 14px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${props => props.$dotColor || '#d1d5db'};
  }
`;

const MiniCard = styled.div`
  background: ${props => props.$bg || '#f9fafb'};
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 6px;
  &:last-child { margin-bottom: 0; }
`;

const MiniCardTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 2px;
`;

const MiniCardDesc = styled.div`
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
`;

const MiniCardMeta = styled.div`
  font-size: 10px;
  color: #9ca3af;
  margin-top: 3px;
  display: flex;
  gap: 8px;
`;

/* ═══════════════════════════════════════════
   BUILD CARDS FROM DATA
   ═══════════════════════════════════════════ */

const buildCards = (data) => {
  const cards = [];

  const summary = cleanMissingDataNote(data.description || data.tripSummary);
  if (summary && summary.trim().length > 0) {
    cards.push({
      id: 'summary', icon: '\u{1F4CB}', label: 'Trip Summary',
      bg: '#fef3c7', content: { type: 'text', text: summary },
    });
  }

  const seasonal = cleanMissingDataNote(data.seasonalHighlights || data.seasonal_highlights);
  if (seasonal && seasonal.trim().length > 0) {
    const month = data.travelMonth || data.travel_month;
    cards.push({
      id: 'seasonal', icon: '\u{1F338}', label: month ? `${month} Highlights` : 'Seasonal',
      bg: '#dbeafe', content: { type: 'text', text: seasonal },
    });
  }

  const transportTips = data.transportationTips || data.transportation_tips || [];
  if (transportTips.length > 0) {
    const mode = data.transportationMode || data.transportation_mode;
    cards.push({
      id: 'transport', icon: '\u{1F68C}', label: mode || 'Getting Around',
      bg: '#e0f2fe', content: { type: 'list', items: transportTips, dotColor: '#38bdf8' },
    });
  }

  const packingTips = data.packingAndSafetyTips || data.tips || [];
  if (packingTips.length > 0) {
    cards.push({
      id: 'packing', icon: '\u{1F9F3}', label: 'Packing & Safety',
      bg: '#f3f4f6', content: { type: 'list', items: packingTips, dotColor: '#9ca3af' },
    });
  }

  const festivals = data.festivalsAndEvents || data.festivals_and_events || [];
  if (festivals.length > 0) {
    cards.push({
      id: 'festivals', icon: '\u{1F389}', label: 'Festivals & Events',
      bg: '#f3e8ff', content: { type: 'list', items: festivals, dotColor: '#a855f7' },
    });
  }

  const localTip = cleanMissingDataNote(data.localPhraseOrTip);
  const surprise = cleanMissingDataNote(data.surpriseOffbeatIdea);
  if ((localTip && localTip.trim().length > 0) || (surprise && surprise.trim().length > 0)) {
    const parts = [];
    if (localTip && localTip.trim().length > 0) parts.push(localTip);
    if (surprise && surprise.trim().length > 0) parts.push(surprise);
    cards.push({
      id: 'local', icon: '\u{1F4A1}', label: 'Local Tips',
      bg: '#fef9c3', content: { type: 'list', items: parts, dotColor: '#f59e0b' },
    });
  }

  const recs = data.specialRecommendations || data.special_recommendations || [];
  if (recs.length > 0) {
    cards.push({
      id: 'recommendations', icon: '\u2B50', label: 'Worth Visiting',
      bg: '#fef3c7', content: { type: 'recommendations', items: recs },
    });
  }

  const dayTrips = data.optionalDayTrips || [];
  if (Array.isArray(dayTrips) && dayTrips.length > 0) {
    cards.push({
      id: 'daytrips', icon: '\u{1F697}', label: 'Day Trips',
      bg: '#fefce8', content: { type: 'daytrips', items: dayTrips },
    });
  }

  return cards;
};

/* ═══════════════════════════════════════════
   RENDER CONTENT
   ═══════════════════════════════════════════ */

const renderContent = (content) => {
  switch (content.type) {
    case 'text':
      return <ContentText>{content.text}</ContentText>;

    case 'list':
      return (
        <ContentList>
          {content.items.map((item, idx) => (
            <ContentListItem key={idx} $dotColor={content.dotColor}>{item}</ContentListItem>
          ))}
        </ContentList>
      );

    case 'recommendations':
      return content.items.map((rec, idx) => (
        <MiniCard key={idx} $bg="#fffbeb">
          <MiniCardTitle>{rec.name}</MiniCardTitle>
          {rec.whyFamous && <MiniCardDesc>{rec.whyFamous}</MiniCardDesc>}
          <MiniCardMeta>
            {rec.estimatedDuration && <span>{rec.estimatedDuration}</span>}
            {rec.estimatedCost && <span>{rec.estimatedCost}</span>}
          </MiniCardMeta>
          {rec.insiderTip && (
            <MiniCardDesc style={{ marginTop: 4, fontStyle: 'italic', color: '#92400e' }}>
              Tip: {rec.insiderTip}
            </MiniCardDesc>
          )}
        </MiniCard>
      ));

    case 'daytrips':
      return content.items.map((trip, idx) => (
        <MiniCard key={idx} $bg="#fefce8">
          <MiniCardTitle>{trip.destination}</MiniCardTitle>
          {(trip.whyVisit || trip.why_visit) && (
            <MiniCardDesc>{trip.whyVisit || trip.why_visit}</MiniCardDesc>
          )}
          <MiniCardMeta>
            {trip.distanceKm && <span>{trip.distanceKm} km</span>}
            {(trip.recommendedDuration || trip.recommended_duration) && (
              <span>{trip.recommendedDuration || trip.recommended_duration}</span>
            )}
          </MiniCardMeta>
        </MiniCard>
      ));

    default:
      return null;
  }
};

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

const TripInfoCards = ({ itineraryData, isMobile, sheetHeight }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const panelRef = useRef(null);

  const cards = buildCards(itineraryData);
  if (cards.length === 0) return null;

  const toggleCategory = (id) => {
    setActiveId(prev => prev === id ? null : id);
  };

  return (
    <Anchor $isMobile={isMobile} $sheetPx={isMobile && typeof sheetHeight === 'number' ? sheetHeight : null}>
      <AnimatePresence>
        {isOpen && (
          <DropdownPanel
            $isMobile={isMobile}
            ref={panelRef}
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <PanelHeader>
              <PanelTitle>Trip Details & Tips</PanelTitle>
              <PanelClose onClick={() => { setIsOpen(false); setActiveId(null); }}>
                {'\u2715'}
              </PanelClose>
            </PanelHeader>

            <PanelScroll>
              {cards.map((card) => {
                const isActive = activeId === card.id;
                return (
                  <div key={card.id}>
                    <CategoryRow $active={isActive} onClick={() => toggleCategory(card.id)}>
                      <CategoryIcon $bg={card.bg}>{card.icon}</CategoryIcon>
                      <CategoryLabel>{card.label}</CategoryLabel>
                      <CategoryChevron $open={isActive}>{'\u203A'}</CategoryChevron>
                    </CategoryRow>

                    <AnimatePresence>
                      {isActive && (
                        <DetailArea
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {renderContent(card.content)}
                        </DetailArea>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </PanelScroll>
          </DropdownPanel>
        )}
      </AnimatePresence>

      <TriggerButton
        $isMobile={isMobile}
        onClick={() => setIsOpen(prev => !prev)}
        whileTap={{ scale: 0.95 }}
      >
        <TriggerIcon>{isOpen ? '\u2715' : '\u{1F4A1}'}</TriggerIcon>
        {!isMobile && !isOpen && <>Tips & Info</>}
        {!isOpen && <Badge>{cards.length}</Badge>}
      </TriggerButton>
    </Anchor>
  );
};

export default TripInfoCards;
